/**
 * BancadaDoAgente — "Experimentar o agente", na Conferência (projeto de agente).
 *
 * O usuário roda um caso do roteiro — ou, com o modelo real, uma mensagem livre — contra a
 * entrega, no sandbox, e vê a resposta e o que o agente fez (ferramentas executadas, passos,
 * confirmação). A rodada que ele quiser guardar vira um caso do roteiro das PRÓXIMAS
 * execuções do projeto: o contrato já aprovado não muda.
 *
 * Cada rodada é uma conversa nova, de uma mensagem. O modelo simulado segue o roteiro (prova
 * o fluxo, não o texto); mensagem livre só existe com o modelo real.
 *
 * `BancadaDoAgente` liga os dados; `Bancada` é a tela, sem rede (é a que os testes exercitam).
 * Paleta C, estilos inline, pt-BR.
 */
import { useState } from 'react';
import { FlaskConical, Play, Trash2 } from 'lucide-react';
import { C } from '@/theme/palette';
import {
  useBancada, usePedirRodada, useGuardarCasoDaBancada, useRemoverCasoDoProjeto,
} from '@/api/executions';
import { paraAApi, paraOFormulario, resumoDoEsperado } from '@/screens/gates/RoteiroDeValidacao';
import { STATUS_DO_CASO } from '@/components/RoteiroSection';

const STATUS_DA_RODADA = {
  pending: ['na fila', C.neutralMuted], running: ['rodando no sandbox…', C.warn],
  done: ['concluída', C.ok], failed: ['não rodou', C.bad],
};
const campo = {
  width: '100%', backgroundColor: C.blackElev2, border: `1px solid ${C.blackBorder}`,
  borderRadius: 6, color: C.neutral, fontSize: 12.5, padding: '7px 9px',
  fontFamily: 'inherit', boxSizing: 'border-box',
};
const rotulo = { fontSize: 11, color: C.neutralMuted, display: 'block', marginBottom: 4 };
const botao = (ativo, primario = true) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 6, fontSize: 12.5,
  fontWeight: 700, padding: '7px 14px', cursor: ativo ? 'pointer' : 'default', opacity: ativo ? 1 : 0.5,
  ...(primario ? { backgroundColor: C.cyber, color: C.black, border: 'none' }
    : { background: 'none', color: C.neutral, border: `1px solid ${C.blackBorder}` }),
});
const n = (v) => Number(v ?? 0).toLocaleString('pt-BR');
const erroDe = (e) => (e ? (e.detail || e.title || e.message || 'Não foi possível concluir.') : '');

function GuardarCaso({ rodada, ferramentas, requisitos, onGuardar, salvando, erro }) {
  const [f, setF] = useState(() => ({ ...paraOFormulario({}), input: rodada.entrada }));
  const set = (chave) => (e) => setF({
    ...f, [chave]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const id = (nome) => `guardar-${rodada.id}-${nome}`;
  const pronto = f.situation.trim() && resumoDoEsperado(paraAApi(f).expect) !== 'sem critério verificável';
  return (
    <div style={{ marginTop: 10, padding: 12, borderRadius: 8, border: `1px solid ${C.blackBorder}` }}>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <div>
          <label htmlFor={id('situacao')} style={rotulo}>Situação</label>
          <input id={id('situacao')} value={f.situation} onChange={set('situation')} style={campo} />
        </div>
        <div>
          <label htmlFor={id('requisito')} style={rotulo}>Requisito</label>
          <select id={id('requisito')} value={f.requirement} onChange={set('requirement')} style={campo}>
            <option value="">—</option>
            {requisitos.map((r) => <option key={r.id} value={r.id}>{r.id} · {r.title}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={id('ferramenta')} style={rotulo}>Ferramenta esperada</label>
          <select id={id('ferramenta')} value={f.tool_called} onChange={set('tool_called')} style={campo}>
            <option value="">—</option>
            {ferramentas.map((t) => <option key={t.id} value={t.name}>{t.id} · {t.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={id('contem')} style={rotulo}>A resposta deve conter (separe com ;)</label>
          <input id={id('contem')} value={f.contains} onChange={set('contains')} style={campo} />
        </div>
        <div>
          <label htmlFor={id('nao-contem')} style={rotulo}>A resposta não pode conter (separe com ;)</label>
          <input id={id('nao-contem')} value={f.not_contains} onChange={set('not_contains')} style={campo} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '10px 0', fontSize: 12.5, color: C.neutralDim }}>
        {[['asks_confirmation', 'Pede confirmação antes de agir'], ['refuses', 'Recusa o pedido']].map(([chave, nome]) => (
          <label key={chave} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={f[chave]} onChange={set(chave)} /> {nome}
          </label>
        ))}
      </div>
      <button type="button" disabled={!pronto || salvando} style={botao(pronto && !salvando)}
        onClick={() => { const caso = paraAApi(f); onGuardar({ runId: rodada.id, situation: caso.situation, expect: caso.expect, requirement: caso.requirement }); }}>
        {salvando ? 'Guardando…' : 'Guardar caso'}
      </button>
      {!pronto && (
        <span style={{ fontSize: 11.5, color: C.neutralMuted, marginLeft: 10 }}>
          Diga a situação e ao menos um critério verificável.
        </span>
      )}
      {erro && <div role="alert" style={{ color: C.bad, fontSize: 12.5, marginTop: 8 }}>{erro}</div>}
    </div>
  );
}

function Rodada({ rodada, ferramentas, requisitos, onGuardar, salvando, erroAoGuardar, podeGuardar }) {
  const [aberto, setAberto] = useState(false);
  const [rot, cor] = STATUS_DA_RODADA[rodada.status] ?? [rodada.status, C.neutralDim];
  const reg = rodada.registro ?? {};
  const veredito = rodada.veredito ? (STATUS_DO_CASO[rodada.veredito.status] ?? [rodada.veredito.status, C.neutralDim]) : null;
  return (
    <li style={{ padding: 12, borderRadius: 8, backgroundColor: C.blackElev, border: `1px solid ${C.blackBorder}` }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span style={{ fontFamily: 'monospace', color: C.cyber, fontSize: 12 }}>{rodada.caseId ?? 'mensagem livre'}</span>
        <span style={{ fontSize: 11.5, color: C.neutralMuted }}>modelo {rodada.modelo}</span>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: cor }}>{rot}</span>
        {veredito && <span style={{ fontSize: 11.5, fontWeight: 700, color: veredito[1] }}>· {veredito[0]}</span>}
      </div>
      <div style={{ fontSize: 12.5, color: C.neutral, marginTop: 6 }}>{rodada.entrada}</div>
      {rodada.status === 'done' && (
        <>
          {rodada.resposta && (
            <pre style={{ fontSize: 12, color: C.neutralDim, whiteSpace: 'pre-wrap', margin: '8px 0 0',
                          padding: 10, borderRadius: 6, backgroundColor: C.blackElev2 }}>{rodada.resposta}</pre>
          )}
          <div style={{ fontSize: 11.5, color: C.neutralMuted, marginTop: 6 }}>
            Ferramentas executadas: {reg.tools_called?.length ? reg.tools_called.join(', ') : 'nenhuma'}
            {reg.confirmation_requested ? ' · pediu confirmação' : ''}
            {reg.steps != null ? ` · ${n(reg.steps)} passo(s)` : ''}
          </div>
          {rodada.modelo === 'simulado' && (
            <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 4 }}>
              Modelo simulado: o texto da resposta não vale como evidência — vale o que o agente fez.
            </div>
          )}
          {rodada.veredito?.detalhe && (
            <div style={{ fontSize: 11.5, color: C.bad, marginTop: 4 }}>{rodada.veredito.detalhe}</div>
          )}
        </>
      )}
      {rodada.status === 'failed' && (
        <div role="alert" style={{ fontSize: 12, color: C.bad, marginTop: 6 }}>{rodada.erro || 'A rodada não rodou.'}</div>
      )}
      {rodada.status === 'done' && podeGuardar && (
        <button type="button" onClick={() => setAberto((a) => !a)} aria-expanded={aberto}
          style={{ ...botao(true, false), marginTop: 10 }}>
          Virar caso do roteiro
        </button>
      )}
      {aberto && (
        <GuardarCaso rodada={rodada} ferramentas={ferramentas} requisitos={requisitos}
          onGuardar={onGuardar} salvando={salvando} erro={erroAoGuardar} />
      )}
    </li>
  );
}

export function Bancada({
  estado, onRodar, rodando, erroAoRodar, onGuardar, salvando, erroAoGuardar, onRemover,
}) {
  const [caso, setCaso] = useState('');
  const [modelo, setModelo] = useState('simulado');
  const [mensagem, setMensagem] = useState('');
  if (!estado) return null;

  const titulo = (
    <h2 style={{ fontSize: 14, fontWeight: 700, color: C.neutral, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
      <FlaskConical size={14} aria-hidden="true" style={{ color: C.cyber }} /> Experimentar o agente
    </h2>
  );
  if (!estado.disponivel) {
    // projeto que não é de agente: a seção não aparece; nos outros casos, diz o motivo
    if (!(estado.casos ?? []).length && !(estado.rodadas ?? []).length) return null;
    return (
      <section aria-label="Experimentar o agente" style={{ marginBottom: 28 }}>
        {titulo}
        <div role="note" style={{ padding: '12px 16px', fontSize: 12.5, color: C.neutralDim,
          border: `1px dashed ${C.blackBorder}`, borderRadius: 8 }}>{estado.motivo}</div>
      </section>
    );
  }

  const real = !!estado.modeloRealHabilitado;
  const emCurso = (estado.rodadas ?? []).some((r) => r.status === 'pending' || r.status === 'running');
  const livre = modelo === 'real' && !caso;
  const pode = !rodando && !emCurso && (caso || (livre && mensagem.trim()));
  const rodar = () => onRodar?.(caso ? { modelo, case_id: caso } : { modelo, entrada: mensagem.trim() });

  return (
    <section aria-label="Experimentar o agente" style={{ marginBottom: 28 }}>
      {titulo}
      <p style={{ fontSize: 12, color: C.neutralMuted, margin: '0 0 12px', maxWidth: '78ch' }}>
        Roda um caso contra a entrega, no sandbox, e mostra o que o agente fez. Cada rodada é
        uma conversa nova, de uma mensagem.
      </p>

      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <div>
          <label htmlFor="bancada-caso" style={rotulo}>Caso do roteiro</label>
          <select id="bancada-caso" value={caso} onChange={(e) => setCaso(e.target.value)} style={campo}>
            <option value="">{modelo === 'real' ? '— mensagem livre —' : '— escolha um caso —'}</option>
            {(estado.casos ?? []).map((c) => <option key={c.id} value={c.id}>{c.id} · {c.situation}</option>)}
          </select>
        </div>
        <div>
          <span style={rotulo}>Modelo</span>
          <div role="radiogroup" aria-label="Modelo" style={{ display: 'flex', gap: 14, fontSize: 12.5, color: C.neutralDim, paddingTop: 6 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="bancada-modelo" checked={modelo === 'simulado'} onChange={() => setModelo('simulado')} />
              Simulado
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: real ? 'pointer' : 'default', opacity: real ? 1 : 0.55 }}>
              <input type="radio" name="bancada-modelo" checked={modelo === 'real'} disabled={!real} onChange={() => setModelo('real')} />
              Real
            </label>
          </div>
          {!real && (
            <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 4 }}>
              O modelo real não está habilitado neste ambiente.
            </div>
          )}
        </div>
      </div>

      {livre && (
        <div style={{ marginTop: 10 }}>
          <label htmlFor="bancada-mensagem" style={rotulo}>Mensagem</label>
          <textarea id="bancada-mensagem" rows={2} value={mensagem} maxLength={4000}
            onChange={(e) => setMensagem(e.target.value)} style={campo}
            placeholder="O que o usuário escreveria para o agente" />
        </div>
      )}
      {modelo === 'real' && (
        <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 6 }}>
          Com o modelo real, a rodada é cobrada na Wallet da execução, em US$.
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={rodar} disabled={!pode} style={botao(!!pode)}>
          <Play size={13} aria-hidden="true" /> {rodando ? 'Enviando…' : 'Rodar'}
        </button>
        {emCurso && <span style={{ fontSize: 11.5, color: C.neutralMuted, marginLeft: 10 }}>Há uma rodada em andamento.</span>}
      </div>
      {erroAoRodar && <div role="alert" style={{ color: C.bad, fontSize: 12.5, marginTop: 8 }}>{erroAoRodar}</div>}

      {(estado.rodadas ?? []).length > 0 && (
        <ul aria-label="Rodadas" style={{ listStyle: 'none', margin: '16px 0 0', padding: 0, display: 'grid', gap: 8 }}>
          {estado.rodadas.map((r) => (
            <Rodada key={r.id} rodada={r} ferramentas={estado.ferramentas ?? []}
              requisitos={estado.requisitos ?? []} onGuardar={onGuardar} salvando={salvando}
              erroAoGuardar={erroAoGuardar} podeGuardar={!!onGuardar} />
          ))}
        </ul>
      )}

      {(estado.casosDoProjeto ?? []).length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.neutralDim, marginBottom: 6 }}>
            Casos guardados no projeto (entram no roteiro da próxima execução)
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
            {estado.casosDoProjeto.map((c) => (
              <li key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, color: C.neutralDim }}>
                <span style={{ flex: 1 }}>
                  {c.situation} — {resumoDoEsperado(c.expect)}
                  {c.requirement ? ` · ${c.requirement}` : ''}
                </span>
                {onRemover && (
                  <button type="button" onClick={() => onRemover(c.id)} aria-label={`Remover o caso ${c.situation}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.neutralMuted }}>
                    <Trash2 size={13} aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export function BancadaDoAgente({ executionId }) {
  const { data } = useBancada(executionId);
  const rodar = usePedirRodada(executionId);
  const guardar = useGuardarCasoDaBancada(executionId);
  const remover = useRemoverCasoDoProjeto(executionId);
  if (!executionId) return null;
  return (
    <Bancada estado={data ?? null}
      onRodar={(pedido) => rodar.mutate(pedido)} rodando={rodar.isPending} erroAoRodar={erroDe(rodar.error)}
      onGuardar={(caso) => guardar.mutate(caso)} salvando={guardar.isPending} erroAoGuardar={erroDe(guardar.error)}
      onRemover={(id) => remover.mutate(id)} />
  );
}

export default BancadaDoAgente;
